/**
 * L2 drill-down: the full attributed change feed, filterable by change type
 * and subscription — filter state in the URL, `Breadcrumbs` back up
 * (dashboard design rules §1 / §4.2).
 *
 * Attribution is Azure's own — a UPN, an app ID, or an Azure service —
 * surfaced verbatim, because "who" is the whole point of this list.
 */

import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { FilterRow } from "@/components/board";
import { type CloudChange, useCloudChanges, useCloudOverview } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { useDrillParams } from "@/lib/drill";

import { ChangeVolumePanel } from "./change-volume-panel";
import { PanelCard } from "./panel-card";
import { CHANGE_TYPE_PARAM, SUB_PARAM } from "./params";
import { CHANGE_TONE, formatTimestamp, shortType } from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Cloud",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to="/cloud" className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Changes", current: true },
];

export function CloudChangesPage() {
  const changes = useCloudChanges();
  const overview = useCloudOverview();
  const drill = useDrillParams(CHANGE_TYPE_PARAM, SUB_PARAM);

  const data = changes.data;
  const types = drill.getList(CHANGE_TYPE_PARAM);
  const subIds = drill.getList(SUB_PARAM);

  const rows = (data?.changes ?? []).filter(
    (change) =>
      (types.length === 0 || types.includes(change.change_type)) &&
      (subIds.length === 0 || subIds.includes(change.subscription_id)),
  );

  const subOptions = (overview.data?.subscriptions ?? []).map((subscription) => ({
    value: subscription.subscription_id,
    label: subscription.name,
  }));

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Resource changes"
        subtitle="Who changed what across the estate, with Azure's own Change Actor attribution."
        actions={
          data?.collected_at ? <Freshness label="Collected" since={data.collected_at} /> : undefined
        }
      />

      {changes.error && (
        <Alert tone="danger" title="Couldn't load the change feed">
          {changes.error instanceof Error ? changes.error.message : "The NOVA API did not respond."}
        </Alert>
      )}

      {changes.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!data.configured ? (
          <PanelCard title="Change feed" caption="Not available">
            <Typography variant="body-sm" tone="muted">
              {data.status_detail}
            </Typography>
          </PanelCard>
        ) : (
          <>
            {/* Above the filter row on purpose: the persisted history keeps no
                per-subscription or per-type split, so it cannot honour these
                filters and must not appear to. */}
            <ChangeVolumePanel changes={data} />

            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: CHANGE_TYPE_PARAM,
                  label: "Change type",
                  noun: "type",
                  options: [
                    { value: "Create", label: "Create" },
                    { value: "Update", label: "Update" },
                    { value: "Delete", label: "Delete" },
                  ],
                },
                {
                  param: SUB_PARAM,
                  label: "Subscription",
                  noun: "subscription",
                  options: subOptions,
                },
              ]}
            />

            <PanelCard
              title="Changes"
              caption={`${formatCount(rows.length)} of ${formatCount(
                data.changes.length,
              )} listed · ${formatCount(data.total)} in ${data.window_hours} hours`}
              padding={rows.length === 0 ? "md" : "none"}
            >
              {rows.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No changes match these filters"
                  description="Clear a filter to widen the list."
                />
              ) : (
                <div className="h-[32rem]">
                  {/* DataGrid needs a definite height for its sticky header/scroller. */}
                  <DataGrid<CloudChange>
                    data={rows}
                    getRowId={(change) => change.id}
                    getRowSearchText={(change) =>
                      `${change.resource_name} ${change.changed_by ?? ""} ${change.subscription_name}`
                    }
                    density="compact"
                    aria-label="All collected resource changes"
                    columns={[
                      {
                        id: "when",
                        header: "When",
                        accessor: (change) => change.changed_at ?? "",
                        sortable: true,
                        cell: (change) => (
                          <span className="text-fg-muted text-xs tabular-nums">
                            {formatTimestamp(change.changed_at)}
                          </span>
                        ),
                      },
                      {
                        id: "type",
                        header: "Change",
                        accessor: (change) => change.change_type,
                        sortable: true,
                        cell: (change) => (
                          <Badge
                            tone={CHANGE_TONE[change.change_type] ?? "neutral"}
                            variant="subtle"
                            size="xs"
                          >
                            {change.change_type}
                          </Badge>
                        ),
                      },
                      {
                        id: "resource",
                        header: "Resource",
                        accessor: (change) => change.resource_name,
                        sortable: true,
                        cell: (change) => (
                          <div className="flex flex-col">
                            <span className="text-fg font-medium">{change.resource_name}</span>
                            {change.resource_type && (
                              <span className="text-fg-muted text-xs">
                                {shortType(change.resource_type)}
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        id: "who",
                        header: "Who",
                        accessor: (change) => change.changed_by ?? "",
                        sortable: true,
                        cell: (change) => (
                          <div className="flex flex-col">
                            <span className="text-fg text-sm">{change.changed_by ?? "—"}</span>
                            {(change.changed_by_type || change.client_type) && (
                              <span className="text-fg-muted text-xs">
                                {[change.changed_by_type, change.client_type]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        id: "subscription",
                        header: "Subscription",
                        accessor: (change) => change.subscription_name,
                        sortable: true,
                        cell: (change) => (
                          <span className="text-fg-muted text-sm">{change.subscription_name}</span>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </PanelCard>

            {(data.truncated || data.total > data.changes.length) && (
              <Typography variant="body-sm" tone="muted">
                Showing the newest {data.changes.length} changes — the window holds{" "}
                {formatCount(data.total)}.
              </Typography>
            )}

            {data.errors.length > 0 && (
              <Typography variant="body-sm" tone="muted">
                Collection problems: {data.errors.join(" · ")}
              </Typography>
            )}
          </>
        ))
      )}
    </main>
  );
}
